import { AutoAwesome, Forward, Info, Star } from '@mui/icons-material'
import { Button, CircularProgress, Grid, TextField, Tooltip } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import {
  getGrantFitSummariesForFunder,
  getSavedGrantFitApplicantDetails,
  markGrantFitApplicantDetailsAsPrimary,
  runGrantFitAnalysis,
  saveGrantFitApplicantDetails
} from 'store/actions/grantfit'
import { selectGrantFit } from 'store/selectors/grantfit'
import { selectFunderProfileInfo } from 'store/selectors/profile'
import { selectUser } from 'store/selectors/user'

import { renderRichText } from 'utils/helpers'
import { trackGrantFitSummarizeButtonClick } from 'utils/mixpanel'

import GrantFitFileUpload from './components/GrantFitFileUpload'
import GrantFitSummary from './components/GrantFitSummary'
import FunderPrograms from 'components/FunderPrograms'

const ProfileGrantFit = ({ gotoTab, programIndex }) => {
  const dispatch = useDispatch()
  const { i18n } = useTranslation()
  const t = i18n.getResourceBundle(i18n.language)

  const { billingPlan } = useSelector(selectUser)
  const { id: funderId, funderPrograms } = useSelector(selectFunderProfileInfo)
  const { savedDetails, summaries, quota, error } = useSelector(selectGrantFit)

  const [state, setState] = useState({
    uploadState: {
      selectedFiles: [],
      errors: null
    },
    applicantText: '',
    savedDetailsId: null,
    savingDetails: false,
    error: null,
    isProcessing: false
  })

  const hasPrograms = funderPrograms?.length > 0
  const summary = hasPrograms
    ? summaries.find(item => item.program === funderPrograms[programIndex].id)
    : summaries.at(0)

  const quotaReached = quota ? quota.used >= quota.allowance : false

  useEffect(() => {
    dispatch(getSavedGrantFitApplicantDetails())
    dispatch(getGrantFitSummariesForFunder(funderId))
  }, [])

  useEffect(() => {
    if (state.isProcessing) {
      if (summary && !summary.isProcessing) {
        setState(state => ({ ...state, isProcessing: false }))
      }
    } else {
      if (summary?.applicantDetails) {
        setState(state => ({
          ...state,
          uploadState: {
            selectedFiles: summary.applicantDetails.documents.map(doc => ({
              id: doc.id,
              name: doc.originalFilename,
              size: doc.fileSize
            })),
            errors: null
          },
          applicantText: summary.applicantDetails.applicantText,
          savedDetailsId: summary.applicantDetails.id,
          error: null
        }))
      } else {
        setState(state => ({
          ...state,
          uploadState: {
            selectedFiles: [],
            errors: null
          },
          applicantText: '',
          savedDetailsId: null,
          error: null
        }))
      }
    }
  }, [summary])

  useEffect(() => {
    if (error) {
      setState(state => ({
        ...state,
        isProcessing: false,
        error: t.funder.grantFit.errors.unknown
      }))
    }
  }, [error])

  const onProgramIndexChange = newIndex => {
    if (state.isProcessing) return
    gotoTab(ProfileGrantFit, [funderPrograms[newIndex].id])
  }

  const setUploadState = uploadState => {
    setState(state => ({ ...state, uploadState, savedDetailsId: null, error: null }))
  }

  const onApplicantTextChange = event => {
    setState(state => ({
      ...state,
      applicantText: event.target.value,
      savedDetailsId: null,
      error: null
    }))
  }

  const validate = () => {
    if (!state.applicantText && state.uploadState.selectedFiles.length === 0) {
      setState(state => ({ ...state, error: t.funder.grantFit.errors.noDetails }))
      return false
    }
    if (state.uploadState.errors?.length > 0) {
      setState(state => ({ ...state, error: t.funder.grantFit.errors.fixFileIssues }))
      return false
    }
    setState(state => ({ ...state, error: null }))
    return true
  }

  const onLoadSavedDetails = () => {
    if (!savedDetails) return
    setState(state => ({
      ...state,
      uploadState: {
        selectedFiles: savedDetails.documents.map(doc => ({
          id: doc.id,
          name: doc.originalFilename,
          size: doc.fileSize
        })),
        errors: null
      },
      applicantText: savedDetails.applicantText,
      savedDetailsId: savedDetails.id,
      error: null
    }))
  }

  const onSaveDetails = async () => {
    if (state.savingDetails) return
    if (!validate()) return

    setState(state => ({ ...state, savingDetails: true }))

    let result
    if (!state.savedDetailsId) {
      result = await dispatch(
        saveGrantFitApplicantDetails({
          applicantText: state.applicantText,
          files: state.uploadState.selectedFiles.filter(file => file.file),
          documents: state.uploadState.selectedFiles.filter(file => file.id),
          primary: true
        })
      )
    } else {
      result = await dispatch(markGrantFitApplicantDetailsAsPrimary(state.savedDetailsId))
    }
    if (result.success) {
      setState(state => ({ ...state, savedDetailsId: result.id, savingDetails: false }))
    } else {
      const error = result.throttled
        ? t.funder.grantFit.errors.tooManyRequests
        : t.funder.grantFit.errors.saveDetailsFailed
      setState(state => ({ ...state, error, savingDetails: false }))
    }
  }

  const onSubmit = async () => {
    if (state.isProcessing) {
      setState(state => ({ ...state, error: t.funder.grantFit.errors.analysisInProgress }))
      return
    }
    if (!validate()) return

    const programId = hasPrograms ? funderPrograms[programIndex].id : undefined

    setState(state => ({ ...state, isProcessing: true }))

    let applicantDetailsId = state.savedDetailsId
    if (!applicantDetailsId) {
      const result = await dispatch(
        saveGrantFitApplicantDetails({
          applicantText: state.applicantText,
          files: state.uploadState.selectedFiles.filter(file => file.file),
          documents: state.uploadState.selectedFiles.filter(file => file.id)
        })
      )
      if (result.success) {
        applicantDetailsId = result.id
        setState(state => ({ ...state, savedDetailsId: applicantDetailsId }))
      } else {
        setState(state => ({
          ...state,
          isProcessing: false,
          error: t.funder.grantFit.errors.saveDetailsFailed
        }))
        return
      }
    }

    dispatch(runGrantFitAnalysis({ funderId, programId, applicantDetailsId }))

    trackGrantFitSummarizeButtonClick(programId)
  }

  const selectBillingPlanVariant = text => (billingPlan === 'Monthly' ? text.monthly : text.default)

  return (
    <Grid container className="profile-grantfit" columns={24}>
      {hasPrograms && (
        <Grid size={{ xs: 24, sm: 7, md: 6, lg: 5 }}>
          <FunderPrograms
            funderPrograms={funderPrograms}
            programIndex={programIndex}
            setProgramIndex={onProgramIndexChange}
          />
        </Grid>
      )}
      <Grid size="grow" className="profile-grantfit-content">
        {quota && (
          <div className="grantfit-quota">
            <p>
              {renderRichText(selectBillingPlanVariant(t.funder.grantFit.quotaNotice), {
                remaining: Math.max(0, quota.allowance - quota.used),
                total: quota.allowance
              })}
            </p>
          </div>
        )}
        <div className="section">
          <p className="intro">{renderRichText(t.funder.grantFit.descriptionText)}</p>
        </div>
        <div className="section">
          <h3>{t.funder.grantFit.projectDetailsTitle}</h3>
          <Button
            className="grantfit-details-button use-saved"
            variant="contained"
            onClick={onLoadSavedDetails}
            disabled={!savedDetails || (savedDetails && savedDetails.id === state.savedDetailsId)}
            startIcon={<Forward />}
          >
            {t.funder.grantFit.projectDetailsUseSaved}
          </Button>
          <p>{t.funder.grantFit.projectDetailsText}</p>
          <ol>
            {t.funder.grantFit.projectDetailsBullets.map((bullet, index) => (
              <li key={index}>{bullet}</li>
            ))}
          </ol>
          <TextField
            className="grantfit-textarea"
            value={state.applicantText}
            onChange={onApplicantTextChange}
            multiline
            minRows={3}
            maxRows={10}
            fullWidth
            placeholder={t.funder.grantFit.projectDetailsPlaceholder}
            slotProps={{ htmlInput: { maxLength: 100000 } }}
            helperText={
              state.applicantText.length === 100000 && t.funder.grantFit.projectDetailsFull
            }
          />
          <div className="upload-title-row">
            <h4>{t.funder.grantFit.uploadDocumentsTitle}</h4>
            <span className="upload-max">{t.funder.grantFit.uploadMaxLabel}</span>
          </div>
          <p>
            <i>{t.funder.grantFit.uploadDocumentsText}</i>
          </p>
          <GrantFitFileUpload uploadState={state.uploadState} setUploadState={setUploadState} />
        </div>
        <Grid container columnGap={2} className={`grantfit-submit ${state.error ? 'error' : ''}`}>
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={state.isProcessing || quotaReached}
            endIcon={state.isProcessing && <CircularProgress size={16} />}
          >
            {t.funder.grantFit.summarizeFitButton}
          </Button>
          <div className="grantfit-save-details">
            <Button
              className="grantfit-details-button save"
              variant="contained"
              onClick={onSaveDetails}
              disabled={
                quotaReached ||
                (savedDetails && savedDetails.id === state.savedDetailsId) ||
                state.savingDetails
              }
              startIcon={<Star />}
              endIcon={state.savingDetails && <CircularProgress size={16} />}
            >
              {t.funder.grantFit.projectDetailsSave}
            </Button>
            <Tooltip
              title={t.funder.grantFit.projectDetailsSaveTooltip}
              placement="top"
              arrow
              slotProps={{ tooltip: { className: 'grantfit-save-tooltip' } }}
            >
              <Info />
            </Tooltip>
          </div>
          {state.isProcessing && <p>{t.funder.grantFit.pleaseWait}</p>}
          {state.error && <p>{state.error}</p>}
        </Grid>
        {quotaReached && (
          <div className="grantfit-quota-error">
            <p>{renderRichText(selectBillingPlanVariant(t.funder.grantFit.quotaReachedError))}</p>
          </div>
        )}
        {funderPrograms?.length >= 2 && (
          <div className="grantfit-note">
            <AutoAwesome fontSize="small" />
            <i>{t.funder.grantFit.programsNotAFit}</i>
          </div>
        )}
        <GrantFitSummary summary={summary} />
      </Grid>
      <div className="grantfit-disclaimer">
        <p>{t.funder.grantFit.disclaimerText}</p>
      </div>
    </Grid>
  )
}

ProfileGrantFit.isEnabled = ({ isFunderDissolved, isLibraryMode }) =>
  !isFunderDissolved && !isLibraryMode
ProfileGrantFit.tabPath = 'grantfit'
ProfileGrantFit.getTabName = t => (
  <>
    <AutoAwesome />
    {t.funder.grantFit.tab}
  </>
)
ProfileGrantFit.requiresProgramParam = true

export default ProfileGrantFit
